import { Article } from '../types';

export const detailedArticles2: Article[] = [
  // ============ MARKETING ============
  {
    id: 'mkt-1',
    title: 'Marketing and Lead Management',
    slug: 'marketing-lead-management',
    description: 'Convert more leads and grow your business with powerful marketing tools',
    category: 'marketing',
    tags: ['marketing', 'leads', 'campaigns', 'email', 'sms'],
    lastUpdated: new Date('2024-01-28'),
    readTime: 13,
    content: `
      <h2>Marketing and Lead Management System</h2>
      <p>Grow your business with integrated marketing tools that help you capture, nurture, and convert leads into loyal customers.</p>
      
      <h3 id="lead-capture">Lead Capture and Management</h3>
      <p>Capture leads from multiple sources and track them through conversion:</p>
      
      <h4>Lead Sources</h4>
      <ul>
        <li><strong>Website Forms</strong> - Automatic import from your website</li>
        <li><strong>Phone Calls</strong> - Log calls as leads</li>
        <li><strong>Social Media</strong> - Facebook, Instagram leads</li>
        <li><strong>Referrals</strong> - Track referral sources</li>
        <li><strong>Walk-ins</strong> - In-person inquiries</li>
        <li><strong>Paid Ads</strong> - Google Ads, Facebook Ads</li>
        <li><strong>Trade Shows</strong> - Event lead capture</li>
      </ul>
      
      <h4>Lead Information</h4>
      <p>Capture comprehensive lead details:</p>
      <ul>
        <li>Contact information (name, phone, email)</li>
        <li>Service interests</li>
        <li>Property details</li>
        <li>Budget range</li>
        <li>Timeline/urgency</li>
        <li>Lead source and campaign</li>
        <li>Lead score/quality rating</li>
        <li>Notes and follow-up tasks</li>
      </ul>
      
      <h3 id="lead-workflow">Lead Workflow and Status</h3>
      <p>Track leads through your sales pipeline:</p>
      <ol>
        <li><strong>New Lead</strong> - Just captured, needs qualification</li>
        <li><strong>Contacted</strong> - Initial contact made</li>
        <li><strong>Qualified</strong> - Confirmed interest and budget</li>
        <li><strong>Estimate Sent</strong> - Proposal delivered</li>
        <li><strong>Negotiating</strong> - Discussing terms</li>
        <li><strong>Won</strong> - Converted to customer</li>
        <li><strong>Lost</strong> - Did not convert</li>
        <li><strong>Nurturing</strong> - Long-term follow-up</li>
      </ol>
      
      <h3 id="lead-assignment">Lead Assignment and Distribution</h3>
      <p>Automatically route leads to the right team members:</p>
      <ul>
        <li><strong>Round-robin assignment</strong> - Distribute evenly among sales team</li>
        <li><strong>Territory-based</strong> - Assign by ZIP code or area</li>
        <li><strong>Service-based</strong> - Route by service expertise</li>
        <li><strong>Performance-based</strong> - Send to top performers</li>
        <li><strong>Manual assignment</strong> - Choose specific rep</li>
      </ul>
      
      <h3 id="follow-up-automation">Automated Follow-up Sequences</h3>
      <p>Never miss a follow-up with automation:</p>
      <ol>
        <li>Create follow-up templates</li>
        <li>Set trigger conditions:
          <ul>
            <li>X hours after lead creation</li>
            <li>No response after Y days</li>
            <li>After estimate sent</li>
            <li>Based on lead score</li>
          </ul>
        </li>
        <li>Configure multi-step sequences:
          <ul>
            <li>Day 1: Welcome email</li>
            <li>Day 2: SMS check-in</li>
            <li>Day 3: Phone call task</li>
            <li>Day 7: Special offer email</li>
            <li>Day 14: Last chance message</li>
          </ul>
        </li>
        <li>Personalize with merge tags</li>
        <li>Stop sequence when responded</li>
      </ol>
      
      <h3 id="customer-lists">Creating Marketing Lists</h3>
      <p>Segment your audience for targeted campaigns:</p>
      
      <h4>List Creation Methods</h4>
      <ul>
        <li><strong>Smart Lists</strong> - Dynamic lists based on criteria</li>
        <li><strong>Static Lists</strong> - Fixed customer selections</li>
        <li><strong>Imported Lists</strong> - Upload from CSV</li>
        <li><strong>Combined Lists</strong> - Merge multiple lists</li>
      </ul>
      
      <h4>Segmentation Criteria</h4>
      <ul>
        <li>Service history (last service date, frequency)</li>
        <li>Geographic location (ZIP, neighborhood)</li>
        <li>Customer value (lifetime value, average ticket)</li>
        <li>Service preferences</li>
        <li>Communication preferences</li>
        <li>Custom tags and properties</li>
        <li>Engagement level (opens, clicks)</li>
      </ul>
      
      <h3 id="email-campaigns">Email Marketing Campaigns</h3>
      <p>Create and send professional email campaigns:</p>
      
      <h4>Campaign Creation</h4>
      <ol>
        <li>Go to Marketing → Campaigns</li>
        <li>Click "New Email Campaign"</li>
        <li>Choose campaign type:
          <ul>
            <li>One-time broadcast</li>
            <li>Automated series</li>
            <li>Newsletter</li>
            <li>Promotional offer</li>
          </ul>
        </li>
        <li>Select recipient list</li>
        <li>Design your email:
          <ul>
            <li>Choose template or start from scratch</li>
            <li>Add content blocks</li>
            <li>Insert images and buttons</li>
            <li>Personalize with merge tags</li>
          </ul>
        </li>
        <li>Preview and test</li>
        <li>Schedule or send immediately</li>
      </ol>
      
      <h4>Email Templates</h4>
      <p>Pre-built templates for common campaigns:</p>
      <ul>
        <li>Seasonal promotions (spring cleaning, holiday specials)</li>
        <li>Service reminders</li>
        <li>Customer win-back</li>
        <li>Referral requests</li>
        <li>Review requests</li>
        <li>Birthday greetings</li>
        <li>New service announcements</li>
        <li>Company newsletters</li>
      </ul>
      
      <h3 id="sms-campaigns">SMS Text Marketing</h3>
      <p>Reach customers instantly with SMS:</p>
      
      <h4>SMS Campaign Setup</h4>
      <ol>
        <li>Ensure SMS opt-in compliance</li>
        <li>Create SMS campaign</li>
        <li>Write concise message (160 characters)</li>
        <li>Include clear call-to-action</li>
        <li>Add tracking link if needed</li>
        <li>Select recipient list</li>
        <li>Schedule send time</li>
      </ol>
      
      <h4>SMS Best Practices</h4>
      <ul>
        <li>Get explicit opt-in consent</li>
        <li>Include opt-out instructions</li>
        <li>Send during appropriate hours</li>
        <li>Keep messages short and valuable</li>
        <li>Use for urgent updates or time-sensitive offers</li>
        <li>Track delivery and response rates</li>
      </ul>
      
      <h3 id="campaign-automation">Marketing Automation</h3>
      <p>Set up triggered campaigns based on customer behavior:</p>
      
      <h4>Automation Triggers</h4>
      <ul>
        <li><strong>Time-based</strong>
          <ul>
            <li>X days since last service</li>
            <li>Service anniversary</li>
            <li>Birthday month</li>
            <li>Seasonal timing</li>
          </ul>
        </li>
        <li><strong>Behavior-based</strong>
          <ul>
            <li>Estimate viewed but not accepted</li>
            <li>Service completed</li>
            <li>Payment received</li>
            <li>Website visit</li>
          </ul>
        </li>
        <li><strong>Event-based</strong>
          <ul>
            <li>New customer welcome</li>
            <li>Service cancellation</li>
            <li>Review submitted</li>
            <li>Referral made</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="campaign-analytics">Campaign Analytics and Reporting</h3>
      <p>Measure campaign effectiveness:</p>
      
      <h4>Email Metrics</h4>
      <ul>
        <li>Delivery rate</li>
        <li>Open rate</li>
        <li>Click-through rate</li>
        <li>Conversion rate</li>
        <li>Unsubscribe rate</li>
        <li>Bounce rate</li>
        <li>Revenue generated</li>
      </ul>
      
      <h4>SMS Metrics</h4>
      <ul>
        <li>Delivery confirmation</li>
        <li>Response rate</li>
        <li>Opt-out rate</li>
        <li>Link clicks</li>
        <li>Conversions</li>
      </ul>
      
      <h4>Lead Metrics</h4>
      <ul>
        <li>Lead volume by source</li>
        <li>Conversion rates by source</li>
        <li>Average time to conversion</li>
        <li>Cost per lead</li>
        <li>Lead quality scores</li>
        <li>Sales team performance</li>
      </ul>
      
      <h3 id="referral-program">Referral Program Management</h3>
      <p>Grow through customer referrals:</p>
      <ol>
        <li>Set up referral incentives:
          <ul>
            <li>Discount for referrer</li>
            <li>Discount for referee</li>
            <li>Cash rewards</li>
            <li>Service credits</li>
          </ul>
        </li>
        <li>Create referral tracking system</li>
        <li>Generate unique referral codes</li>
        <li>Send referral request campaigns</li>
        <li>Track referral sources</li>
        <li>Automate reward fulfillment</li>
        <li>Report on referral performance</li>
      </ol>
      
      <h3 id="review-management">Online Review Management</h3>
      <p>Build your online reputation:</p>
      <ul>
        <li><strong>Review requests</strong> - Automated post-service emails</li>
        <li><strong>Review monitoring</strong> - Track Google, Yelp, Facebook reviews</li>
        <li><strong>Response templates</strong> - Professional review responses</li>
        <li><strong>Review widgets</strong> - Display reviews on website</li>
        <li><strong>Reputation alerts</strong> - Notification of new reviews</li>
        <li><strong>Review analytics</strong> - Track ratings over time</li>
      </ul>
      
      <div class="bg-blue-50 p-4 rounded-lg">
        <strong>Marketing Best Practices:</strong>
        <ul>
          <li>Always get permission before marketing</li>
          <li>Segment your audience for relevance</li>
          <li>Test different messages and timing</li>
          <li>Monitor unsubscribe rates</li>
          <li>Provide value, not just promotions</li>
          <li>Maintain consistent branding</li>
          <li>Track ROI for all campaigns</li>
        </ul>
      </div>
    `,
    tableOfContents: [
      { id: 'lead-capture', title: 'Lead Capture' },
      { id: 'lead-workflow', title: 'Lead Workflow' },
      { id: 'lead-assignment', title: 'Lead Assignment' },
      { id: 'follow-up-automation', title: 'Automated Follow-ups' },
      { id: 'customer-lists', title: 'Marketing Lists' },
      { id: 'email-campaigns', title: 'Email Campaigns' },
      { id: 'sms-campaigns', title: 'SMS Marketing' },
      { id: 'campaign-automation', title: 'Marketing Automation' },
      { id: 'campaign-analytics', title: 'Campaign Analytics' },
      { id: 'referral-program', title: 'Referral Programs' },
      { id: 'review-management', title: 'Review Management' }
    ],
    relatedArticles: ['email-campaigns', 'customer-lists', 'lead-conversion']
  },

  // ============ SERVICE MANAGEMENT ============
  {
    id: 'serv-1',
    title: 'Service Management and Pricing',
    slug: 'service-management-guide',
    description: 'Configure your service offerings, pricing, and estimator tools',
    category: 'services',
    tags: ['services', 'pricing', 'estimates', 'catalog'],
    lastUpdated: new Date('2024-01-30'),
    readTime: 11,
    content: `
      <h2>Service Management System</h2>
      <p>Define and manage your service offerings to streamline operations and ensure consistent pricing across your business.</p>
      
      <h3 id="service-catalog">Building Your Service Catalog</h3>
      <p>Create a comprehensive list of services you offer:</p>
      
      <h4>Adding New Services</h4>
      <ol>
        <li>Go to Settings → Service Items</li>
        <li>Click "Add New Service"</li>
        <li>Enter service details:
          <ul>
            <li><strong>Service Name</strong> - Clear, descriptive name</li>
            <li><strong>Service Code</strong> - Internal reference code</li>
            <li><strong>Category</strong> - Group similar services</li>
            <li><strong>Description</strong> - Detailed service description</li>
            <li><strong>Duration</strong> - Estimated time to complete</li>
            <li><strong>Price</strong> - Standard pricing</li>
          </ul>
        </li>
        <li>Configure pricing options:
          <ul>
            <li>Fixed price</li>
            <li>Hourly rate</li>
            <li>Per square foot</li>
            <li>Per unit</li>
            <li>Custom calculation</li>
          </ul>
        </li>
        <li>Set service requirements:
          <ul>
            <li>Equipment needed</li>
            <li>Skill level required</li>
            <li>Number of technicians</li>
            <li>Materials included</li>
          </ul>
        </li>
        <li>Save service item</li>
      </ol>
      
      <h3 id="service-categories">Service Categories</h3>
      <p>Organize services for easy management:</p>
      
      <h4>Common Categories</h4>
      <ul>
        <li><strong>Window Cleaning</strong>
          <ul>
            <li>Residential interior/exterior</li>
            <li>Commercial storefront</li>
            <li>High-rise windows</li>
            <li>Post-construction cleanup</li>
          </ul>
        </li>
        <li><strong>Pressure Washing</strong>
          <ul>
            <li>Driveway cleaning</li>
            <li>House washing</li>
            <li>Deck and patio</li>
            <li>Commercial buildings</li>
          </ul>
        </li>
        <li><strong>Solar Screens</strong>
          <ul>
            <li>Screen installation</li>
            <li>Screen repair</li>
            <li>Screen removal</li>
            <li>Custom fabrication</li>
          </ul>
        </li>
        <li><strong>Additional Services</strong>
          <ul>
            <li>Gutter cleaning</li>
            <li>Holiday light installation</li>
            <li>Awning cleaning</li>
            <li>Mirror cleaning</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="pricing-structure">Pricing Structure Setup</h3>
      <p>Create flexible pricing that works for your business:</p>
      
      <h4>Pricing Models</h4>
      <ul>
        <li><strong>Flat Rate Pricing</strong>
          <ul>
            <li>Fixed price per service</li>
            <li>Simple and predictable</li>
            <li>Good for standard services</li>
          </ul>
        </li>
        <li><strong>Time-Based Pricing</strong>
          <ul>
            <li>Hourly or daily rates</li>
            <li>Minimum service charges</li>
            <li>Overtime rates</li>
          </ul>
        </li>
        <li><strong>Quantity-Based Pricing</strong>
          <ul>
            <li>Per window/screen</li>
            <li>Per square foot</li>
            <li>Per linear foot</li>
            <li>Volume discounts</li>
          </ul>
        </li>
        <li><strong>Package Pricing</strong>
          <ul>
            <li>Bundled services</li>
            <li>Seasonal packages</li>
            <li>Maintenance plans</li>
          </ul>
        </li>
      </ul>
      
      <h4>Price Modifiers</h4>
      <p>Adjust pricing based on conditions:</p>
      <ul>
        <li>Height/difficulty surcharges</li>
        <li>Rush service fees</li>
        <li>Weekend/holiday rates</li>
        <li>Travel charges for distant locations</li>
        <li>Minimum service charges</li>
        <li>Large job discounts</li>
      </ul>
      
      <h3 id="window-estimator">Window Cleaning Estimator</h3>
      <p>Quick and accurate window cleaning quotes:</p>
      
      <h4>Using the Estimator</h4>
      <ol>
        <li>Go to Estimator → Window Cleaning</li>
        <li>Enter property details:
          <ul>
            <li>Property type (residential/commercial)</li>
            <li>Number of stories</li>
            <li>Approximate square footage</li>
          </ul>
        </li>
        <li>Specify window counts:
          <ul>
            <li>Standard windows</li>
            <li>Large/picture windows</li>
            <li>French doors</li>
            <li>Skylights</li>
            <li>Storm windows</li>
          </ul>
        </li>
        <li>Select service options:
          <ul>
            <li>Interior only</li>
            <li>Exterior only</li>
            <li>Interior and exterior</li>
            <li>Screen cleaning</li>
            <li>Sill and frame cleaning</li>
          </ul>
        </li>
        <li>Add difficulty factors:
          <ul>
            <li>Ladder work required</li>
            <li>Hard water stains</li>
            <li>Paint overspray</li>
            <li>Post-construction</li>
          </ul>
        </li>
        <li>Review calculated price</li>
        <li>Adjust if needed</li>
        <li>Generate estimate document</li>
      </ol>
      
      <h4>Estimator Configuration</h4>
      <p>Customize the estimator for your pricing:</p>
      <ul>
        <li>Set base prices per window type</li>
        <li>Configure difficulty multipliers</li>
        <li>Add location-based adjustments</li>
        <li>Set minimum service amounts</li>
        <li>Create pricing tiers for volume</li>
      </ul>
      
      <h3 id="pressure-washing-estimator">Pressure Washing Estimator</h3>
      <p>Calculate pressure washing jobs accurately:</p>
      
      <h4>Estimator Process</h4>
      <ol>
        <li>Select surface type:
          <ul>
            <li>Concrete driveway</li>
            <li>Wood deck</li>
            <li>Vinyl siding</li>
            <li>Brick/stone</li>
            <li>Stucco</li>
          </ul>
        </li>
        <li>Enter dimensions:
          <ul>
            <li>Square footage</li>
            <li>Or length × width</li>
            <li>Number of surfaces</li>
          </ul>
        </li>
        <li>Specify condition:
          <ul>
            <li>Light cleaning</li>
            <li>Moderate dirt/mildew</li>
            <li>Heavy staining</li>
            <li>Oil stains</li>
            <li>Graffiti removal</li>
          </ul>
        </li>
        <li>Add extras:
          <ul>
            <li>Sealing/treatment</li>
            <li>Gutter cleaning</li>
            <li>Window washing</li>
          </ul>
        </li>
        <li>Calculate and review price</li>
      </ol>
      
      <h3 id="service-packages">Creating Service Packages</h3>
      <p>Bundle services for better value:</p>
      
      <h4>Package Examples</h4>
      <ul>
        <li><strong>Home Exterior Package</strong>
          <ul>
            <li>Window cleaning (exterior)</li>
            <li>Pressure wash driveway</li>
            <li>Gutter cleaning</li>
            <li>10% bundle discount</li>
          </ul>
        </li>
        <li><strong>Spring Cleaning Special</strong>
          <ul>
            <li>Full window cleaning</li>
            <li>Screen cleaning</li>
            <li>Pressure wash patio</li>
            <li>Seasonal pricing</li>
          </ul>
        </li>
        <li><strong>Commercial Maintenance Plan</strong>
          <ul>
            <li>Monthly window cleaning</li>
            <li>Quarterly pressure washing</li>
            <li>Priority scheduling</li>
            <li>Fixed annual rate</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="service-agreements">Service Agreements and Contracts</h3>
      <p>Set up recurring service contracts:</p>
      <ol>
        <li>Create agreement template</li>
        <li>Define service schedule:
          <ul>
            <li>Frequency (weekly/monthly/quarterly)</li>
            <li>Specific services included</li>
            <li>Duration of agreement</li>
          </ul>
        </li>
        <li>Set pricing terms:
          <ul>
            <li>Fixed rate for term</li>
            <li>Price lock guarantee</li>
            <li>Payment schedule</li>
            <li>Cancellation policy</li>
          </ul>
        </li>
        <li>Add terms and conditions</li>
        <li>Enable auto-renewal options</li>
        <li>Track agreement compliance</li>
      </ol>
      
      <h3 id="inventory-integration">Inventory and Supplies Tracking</h3>
      <p>Link services to inventory:</p>
      <ul>
        <li><strong>Materials per Service</strong>
          <ul>
            <li>Define standard supply usage</li>
            <li>Track chemical consumption</li>
            <li>Monitor equipment wear</li>
          </ul>
        </li>
        <li><strong>Automatic Deduction</strong>
          <ul>
            <li>Deduct supplies when service completed</li>
            <li>Low stock alerts</li>
            <li>Reorder suggestions</li>
          </ul>
        </li>
        <li><strong>Cost Tracking</strong>
          <ul>
            <li>Material costs per job</li>
            <li>Profit margin analysis</li>
            <li>Price adjustment recommendations</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="quality-standards">Service Quality Standards</h3>
      <p>Maintain consistent service delivery:</p>
      <ul>
        <li><strong>Service Checklists</strong>
          <ul>
            <li>Step-by-step procedures</li>
            <li>Quality checkpoints</li>
            <li>Safety requirements</li>
            <li>Customer sign-off items</li>
          </ul>
        </li>
        <li><strong>Training Materials</strong>
          <ul>
            <li>Service technique guides</li>
            <li>Video tutorials</li>
            <li>Best practices documentation</li>
          </ul>
        </li>
        <li><strong>Performance Tracking</strong>
          <ul>
            <li>Service completion times</li>
            <li>Customer satisfaction scores</li>
            <li>Callback rates</li>
            <li>Quality audit results</li>
          </ul>
        </li>
      </ul>
      
      <div class="bg-green-50 p-4 rounded-lg">
        <strong>Service Management Tips:</strong>
        <ul>
          <li>Regularly review and update pricing</li>
          <li>Track actual vs. estimated times</li>
          <li>Gather customer feedback on services</li>
          <li>Train staff on all service offerings</li>
          <li>Document special techniques or requirements</li>
          <li>Monitor competitor pricing and services</li>
        </ul>
      </div>
    `,
    tableOfContents: [
      { id: 'service-catalog', title: 'Service Catalog' },
      { id: 'service-categories', title: 'Service Categories' },
      { id: 'pricing-structure', title: 'Pricing Structure' },
      { id: 'window-estimator', title: 'Window Cleaning Estimator' },
      { id: 'pressure-washing-estimator', title: 'Pressure Washing Estimator' },
      { id: 'service-packages', title: 'Service Packages' },
      { id: 'service-agreements', title: 'Service Agreements' },
      { id: 'inventory-integration', title: 'Inventory Integration' },
      { id: 'quality-standards', title: 'Quality Standards' }
    ],
    relatedArticles: ['window-estimator', 'pressure-washing-guide', 'pricing-strategies']
  },

  // ============ EMPLOYEE MANAGEMENT ============
  {
    id: 'emp-1',
    title: 'Employee and Team Management',
    slug: 'employee-management-guide',
    description: 'Manage your team effectively with employee profiles, permissions, and time tracking',
    category: 'admin',
    tags: ['employees', 'team', 'permissions', 'time-tracking', 'payroll'],
    lastUpdated: new Date('2024-02-01'),
    readTime: 12,
    content: `
      <h2>Employee Management System</h2>
      <p>Build and manage a productive team with comprehensive employee management tools that handle everything from onboarding to payroll.</p>
      
      <h3 id="employee-onboarding">Employee Onboarding</h3>
      <p>Set up new team members for success:</p>
      
      <h4>Creating Employee Profiles</h4>
      <ol>
        <li>Go to Admin → Employees</li>
        <li>Click "Add New Employee"</li>
        <li>Enter employee information:
          <ul>
            <li><strong>Personal Details</strong>
              <ul>
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone numbers</li>
                <li>Emergency contact</li>
                <li>Address</li>
              </ul>
            </li>
            <li><strong>Employment Details</strong>
              <ul>
                <li>Employee ID</li>
                <li>Start date</li>
                <li>Position/title</li>
                <li>Department/team</li>
                <li>Employment type (full-time/part-time/contractor)</li>
                <li>Pay rate</li>
              </ul>
            </li>
            <li><strong>System Access</strong>
              <ul>
                <li>Username</li>
                <li>Temporary password</li>
                <li>Role assignment</li>
                <li>Permission level</li>
              </ul>
            </li>
          </ul>
        </li>
        <li>Upload documents:
          <ul>
            <li>Photo ID</li>
            <li>Tax forms (W-4, I-9)</li>
            <li>Certifications</li>
            <li>Insurance documents</li>
          </ul>
        </li>
        <li>Send welcome email with login credentials</li>
      </ol>
      
      <h3 id="roles-permissions">Roles and Permissions</h3>
      <p>Control access with role-based permissions:</p>
      
      <h4>Standard Roles</h4>
      <ul>
        <li><strong>Super Admin</strong>
          <ul>
            <li>Full system access</li>
            <li>All permissions</li>
            <li>System configuration</li>
            <li>User management</li>
          </ul>
        </li>
        <li><strong>Admin</strong>
          <ul>
            <li>Manage employees</li>
            <li>Access all customer data</li>
            <li>Financial reports</li>
            <li>System settings</li>
          </ul>
        </li>
        <li><strong>Manager</strong>
          <ul>
            <li>Schedule appointments</li>
            <li>Manage team members</li>
            <li>View reports</li>
            <li>Approve time sheets</li>
          </ul>
        </li>
        <li><strong>Technician</strong>
          <ul>
            <li>View assigned appointments</li>
            <li>Update job status</li>
            <li>Clock in/out</li>
            <li>Add job notes</li>
          </ul>
        </li>
        <li><strong>Office Staff</strong>
          <ul>
            <li>Customer management</li>
            <li>Appointment scheduling</li>
            <li>Invoice creation</li>
            <li>Basic reporting</li>
          </ul>
        </li>
      </ul>
      
      <h4>Custom Permissions</h4>
      <p>Fine-tune access for each role:</p>
      <ul>
        <li><strong>Customer Data</strong>
          <ul>
            <li>View all customers</li>
            <li>Edit customer info</li>
            <li>Delete customers</li>
            <li>Export customer data</li>
          </ul>
        </li>
        <li><strong>Financial</strong>
          <ul>
            <li>Create invoices</li>
            <li>View financial reports</li>
            <li>Process refunds</li>
            <li>Manage payment methods</li>
          </ul>
        </li>
        <li><strong>Scheduling</strong>
          <ul>
            <li>View all appointments</li>
            <li>Create/edit appointments</li>
            <li>Assign technicians</li>
            <li>Modify schedules</li>
          </ul>
        </li>
        <li><strong>Reporting</strong>
          <ul>
            <li>View reports</li>
            <li>Create custom reports</li>
            <li>Export data</li>
            <li>Access analytics</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="time-tracking">Time and Attendance Tracking</h3>
      <p>Accurate time tracking for payroll and job costing:</p>
      
      <h4>Clock In/Out Methods</h4>
      <ul>
        <li><strong>Web Portal</strong>
          <ul>
            <li>Browser-based clock in/out</li>
            <li>IP address restrictions</li>
            <li>Photo verification</li>
          </ul>
        </li>
        <li><strong>Mobile App</strong>
          <ul>
            <li>GPS location tracking</li>
            <li>Geofencing for job sites</li>
            <li>Offline capability</li>
          </ul>
        </li>
        <li><strong>Manual Entry</strong>
          <ul>
            <li>Manager timesheet entry</li>
            <li>Bulk time import</li>
            <li>Adjustment requests</li>
          </ul>
        </li>
      </ul>
      
      <h4>Time Tracking Features</h4>
      <ul>
        <li>Automatic break deduction</li>
        <li>Overtime calculation</li>
        <li>Job-specific time allocation</li>
        <li>Travel time tracking</li>
        <li>PTO and sick time accrual</li>
        <li>Holiday tracking</li>
        <li>Timesheet approval workflow</li>
      </ul>
      
      <h3 id="scheduling-dispatch">Scheduling and Dispatch</h3>
      <p>Efficiently assign and manage field teams:</p>
      
      <h4>Team Scheduling</h4>
      <ul>
        <li><strong>Availability Management</strong>
          <ul>
            <li>Set working hours per employee</li>
            <li>Block out time off</li>
            <li>Manage vacation requests</li>
            <li>Track certifications/skills</li>
          </ul>
        </li>
        <li><strong>Smart Assignment</strong>
          <ul>
            <li>Match skills to service requirements</li>
            <li>Consider location and travel time</li>
            <li>Balance workload across team</li>
            <li>Respect customer preferences</li>
          </ul>
        </li>
        <li><strong>Real-time Dispatch</strong>
          <ul>
            <li>GPS tracking of field teams</li>
            <li>Push notifications for new jobs</li>
            <li>Route optimization</li>
            <li>Emergency reassignment</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="performance-tracking">Performance Management</h3>
      <p>Monitor and improve team performance:</p>
      
      <h4>Key Performance Indicators</h4>
      <ul>
        <li><strong>Productivity Metrics</strong>
          <ul>
            <li>Jobs completed per day</li>
            <li>Average job duration</li>
            <li>Efficiency vs. estimates</li>
            <li>Utilization rate</li>
          </ul>
        </li>
        <li><strong>Quality Metrics</strong>
          <ul>
            <li>Customer satisfaction scores</li>
            <li>Callback/redo rate</li>
            <li>Complaint frequency</li>
            <li>Quality audit scores</li>
          </ul>
        </li>
        <li><strong>Revenue Metrics</strong>
          <ul>
            <li>Revenue per technician</li>
            <li>Upsell success rate</li>
            <li>Average ticket value</li>
            <li>Collection rate</li>
          </ul>
        </li>
      </ul>
      
      <h4>Performance Reviews</h4>
      <ol>
        <li>Schedule regular reviews (monthly/quarterly)</li>
        <li>Gather performance data</li>
        <li>Document achievements and areas for improvement</li>
        <li>Set goals and objectives</li>
        <li>Create development plans</li>
        <li>Track progress over time</li>
      </ol>
      
      <h3 id="payroll-integration">Payroll Processing</h3>
      <p>Streamline payroll with integrated time data:</p>
      
      <h4>Payroll Features</h4>
      <ul>
        <li><strong>Automatic Calculations</strong>
          <ul>
            <li>Regular hours</li>
            <li>Overtime (1.5x, 2x)</li>
            <li>Commission/bonuses</li>
            <li>Deductions</li>
          </ul>
        </li>
        <li><strong>Pay Types</strong>
          <ul>
            <li>Hourly wages</li>
            <li>Salary</li>
            <li>Piece rate</li>
            <li>Commission-based</li>
          </ul>
        </li>
        <li><strong>Export Options</strong>
          <ul>
            <li>QuickBooks integration</li>
            <li>ADP export</li>
            <li>Paychex format</li>
            <li>Custom CSV export</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="training-certifications">Training and Certifications</h3>
      <p>Track employee qualifications and training:</p>
      <ul>
        <li><strong>Certification Tracking</strong>
          <ul>
            <li>License numbers and expiry dates</li>
            <li>Renewal reminders</li>
            <li>Required vs. optional certs</li>
            <li>Compliance reporting</li>
          </ul>
        </li>
        <li><strong>Training Records</strong>
          <ul>
            <li>Completed training modules</li>
            <li>Safety training dates</li>
            <li>Skill assessments</li>
            <li>Training materials library</li>
          </ul>
        </li>
      </ul>
      
      <h3 id="communication-tools">Team Communication</h3>
      <p>Keep your team connected and informed:</p>
      <ul>
        <li><strong>Announcements</strong> - Broadcast important updates</li>
        <li><strong>Team Chat</strong> - Real-time messaging</li>
        <li><strong>Job Notes</strong> - Share job-specific information</li>
        <li><strong>Document Sharing</strong> - Policies, procedures, forms</li>
        <li><strong>Schedule Notifications</strong> - Automatic schedule updates</li>
        <li><strong>Emergency Alerts</strong> - Urgent communications</li>
      </ul>
      
      <div class="bg-yellow-50 p-4 rounded-lg">
        <strong>Employee Management Best Practices:</strong>
        <ul>
          <li>Maintain up-to-date emergency contacts</li>
          <li>Regular permission audits for security</li>
          <li>Clear communication of expectations</li>
          <li>Consistent performance feedback</li>
          <li>Proper documentation of all issues</li>
          <li>Regular training and development</li>
          <li>Fair and transparent scheduling</li>
        </ul>
      </div>
    `,
    tableOfContents: [
      { id: 'employee-onboarding', title: 'Employee Onboarding' },
      { id: 'roles-permissions', title: 'Roles & Permissions' },
      { id: 'time-tracking', title: 'Time Tracking' },
      { id: 'scheduling-dispatch', title: 'Scheduling & Dispatch' },
      { id: 'performance-tracking', title: 'Performance Management' },
      { id: 'payroll-integration', title: 'Payroll Processing' },
      { id: 'training-certifications', title: 'Training & Certifications' },
      { id: 'communication-tools', title: 'Team Communication' }
    ],
    relatedArticles: ['time-tracking', 'role-permissions', 'performance-management']
  }
];